import { createSlice, PayloadAction, Reducer } from '@reduxjs/toolkit';

import { ACCOUNT_SLICE_NAME, ACCOUNT_INITIAL_STATE } from './consts';
import type { AccountState } from './types';
import { fetchAccountData, initializeAccountData, refreshAccountData } from './thunks';

const accountSlice = createSlice({
  name: ACCOUNT_SLICE_NAME,
  initialState: ACCOUNT_INITIAL_STATE,
  reducers: {
    selectCompany: (state, action: PayloadAction<string>) => {
      state.currentCompanyId = action.payload;
      state.dataStale = true;
    },

    markDataAsStale: (state) => {
      state.dataStale = true;
    },

    clearAccountData: () => ({ ...ACCOUNT_INITIAL_STATE }),

    updateIsReadyToUseLK: (state, action: PayloadAction<boolean>) => {
      state.isReadyToUseLK = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccountData.fulfilled, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.dataStale = false;

        state.userId = action.payload.userId || '';
        state.currentCompanyId = action.payload.currentCompanyId || '';
        state.companyIds = action.payload.companyIds;
        state.isBillingEnabled = action.payload.isBillingEnabled;
        state.streamLibraryId = action.payload.streamLibraryId || '';
        state.isReadyToUseLK = action.payload.isReadyToUseLK;
        state.isAuthorized = true;
      })
      .addCase(fetchAccountData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch account data';

        if (action.payload?.includes('Unauthorized') || action.payload?.includes('Authentication')) {
          state.isAuthorized = false;
          state.userId = '';
          state.companyIds = [];
          state.streamLibraryId = '';
          state.isBillingEnabled = false;
        }
      })

      .addCase(initializeAccountData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeAccountData.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload !== null) {
          state.lastFetchTime = Date.now();
          state.dataStale = false;

          state.userId = action.payload.userId || '';
          state.currentCompanyId = action.payload.currentCompanyId || '';
          state.companyIds = action.payload.companyIds;
          state.isBillingEnabled = action.payload.isBillingEnabled;
          state.streamLibraryId = action.payload.streamLibraryId || '';
          state.isReadyToUseLK = action.payload.isReadyToUseLK;
          state.isAuthorized = true;
        }
      })
      .addCase(initializeAccountData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to initialize account data';

        if (action.payload?.includes('Unauthorized') || action.payload?.includes('Authentication')) {
          state.isAuthorized = false;
          state.userId = '';
          state.companyIds = [];
          state.streamLibraryId = '';
          state.isBillingEnabled = false;
        }
      })

      .addCase(refreshAccountData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshAccountData.fulfilled, (state, action) => {
        state.loading = false;
        state.lastFetchTime = Date.now();
        state.dataStale = false;

        state.userId = action.payload.userId || '';
        state.currentCompanyId = action.payload.currentCompanyId || '';
        state.companyIds = action.payload.companyIds;
        state.isBillingEnabled = action.payload.isBillingEnabled;
        state.streamLibraryId = action.payload.streamLibraryId || '';
        state.isReadyToUseLK = action.payload.isReadyToUseLK;
        state.isAuthorized = true;
      })
      .addCase(refreshAccountData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to refresh account data';

        if (action.payload?.includes('Unauthorized') || action.payload?.includes('Authentication')) {
          state.isAuthorized = false;
          state.userId = '';
          state.companyIds = [];
          state.streamLibraryId = '';
          state.isBillingEnabled = false;
        }
      });
  },
});

export const { selectCompany, markDataAsStale, clearAccountData, updateIsReadyToUseLK, clearError, setLoading } =
  accountSlice.actions;

export const accountReducer: Reducer<AccountState> = accountSlice.reducer;
